import React, {useCallback, useMemo, useState} from "react";
import 'react-calendar-timeline/lib/Timeline.css';
import {Box, LinearProgress} from "@mui/material";
import {Outlet, useNavigate} from "react-router-dom";

import usePerformerShifts from "../hooks/usePerformerShifts";
import usePerformerData from "../hooks/usePerformerData";
import useBuffer from "../hooks/useBuffer";
import useDataReloadForSchedule from "../hooks/useDataReloadForSchedule";
import {useTimezone} from "../hooks/useTimezone";
import useOpenUrlInNewTab from "../hooks/useOpenUrlInNewTab";

import TimeslotAndSessionsTimeline from "../components/TimeslotAndSessionsTimeline";
import Loader from "../components/Loader";
import buildSortingDefinitions from "../helpers/ScheduleUtils";
import SchedulePageHeader from "../components/SchedulePageHeader";
import {NAVIGATION_PATHS} from "../config/NavigationPaths";
import {buildMemberLookupUrl} from "./ParentInfoLookupView";

export default function ManageSchedule() {
    const {timezone} = useTimezone();
    const openUrlInNewTab = useOpenUrlInNewTab();

    const timelineOptions = {
        enableContextMenu: true,
        enableSessionHealthDetailDisplay: true,
        enableSessionBookedHealthIndicator: true,
        enableZoomLicenseHealthIndicator: true,
        enableZoomLinkHealthIndicator: true,
        enableCohostsAddedHealthIndicator: true,
        enablePerformerCheckInHealthIndicator: true,
        enablePerformerJoinedHealthIndicator: true,
        enableRecordingsAvailableHealthIndicator: true,
        enablePollingSessionHealthData: true,
        enablePollingShiftCheckInData: true
    };

    const navigate = useNavigate();

    const {
        handleManualRefresh,
        handleTimelineBoundsChanged,
        handleTimelineTimeChanged,
        setAreShiftsFiltered,
        areShiftsFiltered,
        isShiftDataLoading,
        shiftData,
        currentViewType,
        lastVisibleBounds,
        serviceTypeOptions,
        selectedServiceTypeKeycodes,
        setSelectedServiceTypeKeycodes,
    } = usePerformerShifts();
    const {isPerformerDataLoading, performerDataLoadingError, performerData} = usePerformerData();
    const sortingDefinitions = buildSortingDefinitions(shiftData, lastVisibleBounds);
    const [sortOption, handleSortOptionUpdate] = useState(Object.keys(sortingDefinitions).find(key => sortingDefinitions[key].default));
    const {bufferSize} = useBuffer(performerData, timezone);

    useDataReloadForSchedule(handleTimelineBoundsChanged);

    const sortedPerformerData = useMemo(() => {
        return [...performerData].sort(sortingDefinitions[sortOption].sortingFunction);
    }, [performerData, sortOption, lastVisibleBounds, currentViewType, sortingDefinitions]);

    const handleTimelineDoubleClick = useCallback((groupId, time, e) => {
        const userInfo = performerData.find(user => user.id === groupId);
        navigate(NAVIGATION_PATHS.NEW_SHIFT, {
            state: {
                userName: userInfo.displayName,
                userId: userInfo.id,
                chosenStartTime: time,
                isEditMode: false,
                currentViewType
            }
        });
    }, [performerData]);

    const lookupMember = useCallback((memberId) => {
        const url = buildMemberLookupUrl(memberId);
        openUrlInNewTab(url);
    }, [openUrlInNewTab]);
    
    if (isPerformerDataLoading || !performerData) {
        return <Loader />;
    }

    if (performerDataLoadingError) {
        return `Request Error! ${performerDataLoadingError.message}`;
    }

    return (
        <Box id="performer-calendar">
            <SchedulePageHeader
                sortOption={sortOption}
                setAreShiftsFiltered={setAreShiftsFiltered}
                areShiftsFiltered={areShiftsFiltered}
                loading={isShiftDataLoading}
                handleSortOptionUpdate={handleSortOptionUpdate}
                sortingDefinitions={sortingDefinitions}
                handleManualRefresh={handleManualRefresh}
                availableServiceTypes={serviceTypeOptions}
                selectedServiceTypeKeycodes={selectedServiceTypeKeycodes}
                setSelectedServiceTypeKeycodes={setSelectedServiceTypeKeycodes}/>

            <Outlet />

            {isShiftDataLoading && <LinearProgress />}
            {!isShiftDataLoading && <Box sx={{my: 0.5}}></Box>}

            <TimeslotAndSessionsTimeline
                performerData={sortedPerformerData}
                shiftData={shiftData}
                currentViewType={currentViewType}
                bufferSize={bufferSize}
                onChildDetailsDrilledInto={lookupMember}
                onTimelineDoubleClick={handleTimelineDoubleClick}
                onTimelineBoundsChanged={handleTimelineBoundsChanged}
                onTimelineTimeChanged={handleTimelineTimeChanged}
                options={timelineOptions}
                currentSortingDefinition={sortingDefinitions?.[sortOption]} />
        </Box>
    );
}